import { CartAddEvent } from '@theme/events';

/**
 * kokoot · "Complete the Set" bundle widget.
 * - Per-card qty steppers and ‹ › swap arrows (cycle the pre-rendered same-collection pool).
 * - Recomputes the combo total (price × qty for checked cards) + discount display.
 * - "Add all" batches the checked cards (each × its qty) in one /cart/add.js call and
 *   dispatches CartAddEvent so the cart drawer/page + reward bar morph.
 */
class KokootCompleteTheSet extends HTMLElement {
  connectedCallback() {
    try { this.pool = JSON.parse(this.dataset.pool || '[]'); } catch (e) { this.pool = []; }
    this.totalEl = this.querySelector('[data-kkset-total]');
    this.origEl = this.querySelector('[data-kkset-orig]');
    this.saveAmtEl = this.querySelector('[data-kkset-saveamt]');
    this.addBtn = this.querySelector('[data-kkset-add]');
    this.liveEl = this.querySelector('[data-kkset-live]');
    this.pct = Number(this.dataset.discountPercent) || 0;

    const ref = ((this.origEl || this.totalEl)?.textContent || '$0.00').trim();
    const m = ref.match(/^([^\d.,\s]*)\s*[\d.,]+\s*(.*)$/);
    this.prefix = m ? m[1] : '$';
    this.suffix = m ? m[2] : '';
    this.addBtnLabel = this.addBtn ? this.addBtn.textContent : 'Add all to cart';

    this.addEventListener('change', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('kkset__chk')) this.recalc();
    });
    this.addEventListener('click', (e) => this.onClick(e));
    this.recalc();
  }

  money(cents) {
    const n = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${this.prefix}${n}${this.suffix}`;
  }

  cards() {
    return Array.from(this.querySelectorAll('[data-kkset-item]'));
  }

  qtyOf(card) {
    const q = card.querySelector('[data-kkset-qty]');
    return Math.max(1, parseInt(q && q.textContent, 10) || 1);
  }

  shownVariantIds() {
    return this.cards().map((c) => c.querySelector('.kkset__chk')?.dataset.variantId);
  }

  onClick(e) {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const qb = target.closest('.kkset__qbtn');
    if (qb) { e.preventDefault(); this.stepQty(qb); return; }
    const sw = target.closest('[data-kkset-swap]');
    if (sw) { e.preventDefault(); this.swap(sw); return; }
    const add = target.closest('[data-kkset-add]');
    if (add) { e.preventDefault(); this.addAll(); return; }
  }

  stepQty(btn) {
    const card = btn.closest('[data-kkset-item]');
    const q = card?.querySelector('[data-kkset-qty]');
    if (!q) return;
    let v = (parseInt(q.textContent, 10) || 1) + Number(btn.dataset.qstep);
    v = Math.min(10, Math.max(1, v));
    q.textContent = String(v);
    this.recalc();
  }

  swap(btn) {
    if (!this.pool.length) return;
    const card = btn.closest('[data-kkset-item]');
    if (!card) return;
    const chk = card.querySelector('.kkset__chk');
    const step = Number(btn.dataset.kksetSwap) || 1;
    let idx = parseInt(card.dataset.altIndex, 10) || 0;
    const len = this.pool.length;
    const taken = this.shownVariantIds().filter((id) => id !== chk.dataset.variantId);

    let chosen = -1;
    for (let n = 1; n <= len; n++) {
      const cand = (((idx + step * n) % len) + len) % len;
      const item = this.pool[cand];
      if (item && taken.indexOf(String(item.v)) === -1) { chosen = cand; break; }
    }
    if (chosen === -1) return;
    const it = this.pool[chosen];
    card.dataset.altIndex = String(chosen);
    chk.dataset.variantId = it.v;
    chk.dataset.price = it.p;
    if (!chk.checked) chk.checked = true;

    const img = card.querySelector('[data-kkset-img]');
    if (img) img.innerHTML = it.i ? `<img src="${it.i}" alt="${String(it.t || '').replace(/"/g, '&quot;')}" loading="lazy">` : '';
    const nm = card.querySelector('[data-kkset-name]');
    if (nm) nm.textContent = it.t || '';
    const pr = card.querySelector('[data-kkset-price]');
    if (pr) pr.textContent = this.money(it.p);
    const q = card.querySelector('[data-kkset-qty]');
    if (q) q.textContent = '1';
    this.recalc();
  }

  recalc() {
    let sum = 0;
    this.cards().forEach((card) => {
      const chk = card.querySelector('.kkset__chk');
      if (!chk || !chk.checked) return;
      sum += (Number(chk.dataset.price) || 0) * this.qtyOf(card);
    });

    if (this.totalEl) {
      if (this.pct > 0) {
        const save = Math.round((sum * this.pct) / 100);
        this.totalEl.textContent = this.money(sum - save);
        if (this.origEl) this.origEl.textContent = this.money(sum);
        if (this.saveAmtEl) this.saveAmtEl.textContent = `you save ${this.money(save)}`;
      } else {
        this.totalEl.textContent = this.money(sum);
      }
    }
    if (this.addBtn) {
      this.addBtn.disabled = this.cards().every((c) => {
        const k = c.querySelector('.kkset__chk');
        return !k || !k.checked;
      });
    }
  }

  collectItems() {
    const items = [];
    this.cards().forEach((card) => {
      const chk = card.querySelector('.kkset__chk');
      if (!chk || !chk.checked) return;
      let id = chk.dataset.variantId;
      if (chk.dataset.current === 'true') {
        const live = /** @type {HTMLInputElement|null} */ (document.querySelector('product-form-component input[name="id"]'));
        if (live?.value) id = live.value;
      }
      if (!id) return;
      items.push({ id: Number(id), quantity: this.qtyOf(card) });
    });
    return items;
  }

  sectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  async addAll() {
    const items = this.collectItems();
    if (!items.length || !this.addBtn) return;
    this.addBtn.disabled = true;
    const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.sectionIds() }),
      });
      const data = await res.json();
      if (data.status) {
        if (this.liveEl) this.liveEl.textContent = data.description || data.message || 'Could not add to cart';
        this.addBtn.disabled = false;
        return;
      }
      this.dispatchEvent(
        new CartAddEvent(undefined, this.dataset.sectionId || 'kokoot-complete-the-set', {
          source: 'kokoot-complete-the-set',
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          sections: data.sections,
        })
      );
      if (this.liveEl) this.liveEl.textContent = (window.Theme?.translations?.added) || 'Added to cart';
      this.addBtn.textContent = '✓';
      setTimeout(() => {
        if (!this.addBtn) return;
        this.addBtn.textContent = this.addBtnLabel;
        this.addBtn.disabled = false;
      }, 1200);
    } catch (e) {
      console.error('[kokoot-complete-the-set] add failed', e);
      this.addBtn.disabled = false;
    }
  }
}

if (!customElements.get('kokoot-complete-the-set')) {
  customElements.define('kokoot-complete-the-set', KokootCompleteTheSet);
}
